import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import Spinner from '../components/Spinner';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { ArrowDownTrayIcon, ShieldCheckIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';

const VerifyCertificatePage = () => {
    const { certId } = useParams();
    const [inputId, setInputId] = useState('');
    const [certificate, setCertificate] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const certificateRef = useRef(null);

    const verifyCertificate = async (idToVerify) => {
        if (!idToVerify) return;
        setLoading(true);
        setError('');
        setCertificate(null);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/certificates/verify/${idToVerify}`);
            setCertificate(res.data);
        } catch (err) {
            setError(err.response?.data?.msg || "An error occurred during verification.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (certId) {
            setInputId(certId);
            verifyCertificate(certId);
        }
    }, [certId]);

    const handleSubmit = (e) => { e.preventDefault(); verifyCertificate(inputId); };

    const handleDownloadPDF = async () => {
        if (!certificateRef.current || !certificate) return;
        
        setDownloading(true);
        const loadingToast = toast.loading('Preparing PDF...');
        
        try {
            const element = certificateRef.current;
            
            // Make element visible temporarily
            const parentDiv = element.parentElement;
            parentDiv.classList.remove('hidden');
            parentDiv.style.position = 'absolute';
            parentDiv.style.left = '-9999px';
            parentDiv.style.top = '0';
            parentDiv.style.visibility = 'visible';
            
            // Wait for fonts and images to load
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: true,
                backgroundColor: '#ffffff',
                width: 1122,
                height: 794,
                allowTaint: true,
                foreignObjectRendering: false
            });
            
            // Hide element again
            parentDiv.classList.add('hidden');
            parentDiv.style.position = '';
            parentDiv.style.left = '';
            parentDiv.style.top = '';
            parentDiv.style.visibility = '';
            
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });
            
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
            pdf.save(`Certificate_${certificate.certificateId}.pdf`);
            
            toast.success('Certificate downloaded successfully!', { id: loadingToast });
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF. Please try again.', { id: loadingToast });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            {certificate && (
                <SEO 
                    title={`Certificate - ${certificate.userName} | AircoreDynamics`}
                    description={`Verified certificate for ${certificate.userName} who completed ${certificate.courseName} at AircoreDynamics with ${certificate.score}% score.`}
                    keywords="certificate, course completion, aviation training, aerospace education"
                    url={`/verify/${certificate.certificateId}`}
                    type="article"
                />
            )}
            <div className="min-h-screen bg-gradient-to-br from-light-bg via-light-card to-light-bg dark:from-dark-bg dark:via-dark-card dark:to-dark-bg">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-4xl mx-auto pt-4 md:pt-8 pb-8 px-4"
                >
                    {/* Header Section */}
                    <div className="text-center mb-4 md:mb-6">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                            className="inline-block mb-3"
                        >
                            <div className="bg-gradient-to-br from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-500 p-2.5 md:p-3 rounded-xl shadow-lg">
                                <ShieldCheckIcon className="h-8 w-8 md:h-10 md:w-10 text-white" />
                            </div>
                        </motion.div>
                        <h1 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                            Certificate Verification
                        </h1>
                        <p className="text-sm md:text-base opacity-70 max-w-xl mx-auto">
                            Verify the authenticity of AircoreDynamics certificates
                        </p>
                    </div>

                    {/* Search Card */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white dark:bg-dark-card rounded-xl shadow-lg border border-light-border dark:border-dark-border p-4 md:p-5 mb-5"
                    >
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div className="relative">
                                <input 
                                    type="text" 
                                    value={inputId} 
                                    onChange={(e) => setInputId(e.target.value)} 
                                    placeholder="Enter Certificate ID (e.g., ACD-XXXX-XXXX)" 
                                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text focus:border-light-primary dark:focus:border-dark-primary focus:outline-none transition-all text-sm md:text-base font-mono"
                                />
                                <ShieldCheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 opacity-30" />
                            </div>
                            <button 
                                type="submit" 
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-500 text-white font-semibold py-2.5 md:py-3 rounded-lg hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheckIcon className="h-4 w-4 md:h-5 md:w-5" />
                                        <span>Verify Certificate</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </motion.div>

                    {loading && (
                        <div className="flex justify-center py-8">
                            <Spinner />
                        </div>
                    )}
                    
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-4 text-center"
                        >
                            <div className="inline-block p-2 bg-red-100 dark:bg-red-900/40 rounded-full mb-2">
                                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <p className="text-red-700 dark:text-red-300 font-semibold text-sm md:text-base">{error}</p>
                        </motion.div>
                    )}

            {certificate && (
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-4"
                >
                    {/* Hidden PDF Certificate (Only for Download) */}
                    <div className="hidden">
                        <div 
                            ref={certificateRef}
                            className="relative bg-white overflow-hidden"
                            style={{ 
                                width: '1122px',
                                height: '794px',
                                fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}
                        >
                            {/* Border Frame */}
                            <div className="absolute inset-0 border-8 border-gray-800 m-8"></div>
                            <div className="absolute inset-0 border-2 border-gray-300 m-12"></div>

                            {/* Content Container */}
                            <div className="relative h-full flex flex-col" style={{ padding: '60px 80px' }}>
                                {/* Header with Logo */}
                                <div className="flex items-center justify-between mb-8">
                                    <div className="flex items-center gap-3">
                                        <img 
                                            src="/logo-dark.png" 
                                            alt="AircoreDynamics Logo" 
                                            style={{ height: '50px', width: 'auto' }}
                                        />
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-800">AircoreDynamics</h2>
                                            <p className="text-xs text-gray-600">Aviation Education Platform</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center justify-end gap-2 text-green-600 mb-1">
                                            <ShieldCheckIcon className="h-4 w-4" />
                                            <span className="text-xs font-bold">VERIFIED</span>
                                        </div>
                                        <p className="text-xs text-gray-500">ID: {certificate.certificateId}</p>
                                    </div>
                                </div>

                                {/* Certificate Title */}
                                <div className="text-center mb-10">
                                    <h1 className="text-5xl font-bold text-gray-800 mb-2 tracking-wider" style={{ fontFamily: 'serif' }}>
                                        CERTIFICATE
                                    </h1>
                                    <p className="text-xl text-gray-600 tracking-widest">OF COMPLETION</p>
                                    <div className="w-32 h-1 bg-gray-800 mx-auto mt-3"></div>
                                </div>

                                {/* Certificate Body */}
                                <div className="text-center space-y-5 flex-1 flex flex-col justify-center pb-8">
                                    <p className="text-lg text-gray-700">This is to certify that</p>
                                    
                                    <div className="my-2">
                                        <p className="text-5xl font-bold text-gray-800 mb-3" style={{ fontFamily: 'serif' }}>
                                            {certificate.user.name}
                                        </p>
                                        <div className="w-64 h-0.5 bg-gray-400 mx-auto"></div>
                                    </div>

                                    <p className="text-lg text-gray-700">has successfully completed</p>

                                    <div className="my-3">
                                        <p className="text-3xl font-semibold text-gray-800 px-12 leading-tight">
                                            {certificate.course.title}
                                        </p>
                                    </div>

                                    <p className="text-sm text-gray-600 max-w-xl mx-auto leading-relaxed pt-2">
                                        and has demonstrated comprehensive understanding and proficiency in all 
                                        course requirements and assessments.
                                    </p>
                                    
                                    <p className="text-xs text-gray-500 mt-4">
                                        Course material indexed in MERLOT II (ID: 773476432)
                                    </p>
                                </div>

                                {/* Footer */}
                                <div className="border-t-2 border-gray-300 pt-5">
                                    <div className="flex justify-between items-end">
                                        <div className="text-left">
                                            <p className="text-xs text-gray-500 mb-2">Date of Completion</p>
                                            <div className="w-44 border-b-2 border-gray-800 pb-1 mb-1">
                                                <p className="font-semibold text-gray-800 text-sm">
                                                    {new Date(certificate.issuedAt).toLocaleDateString('en-US', { 
                                                        month: 'long', 
                                                        day: 'numeric', 
                                                        year: 'numeric' 
                                                    })}
                                                </p>
                                            </div>
                                            <p className="text-xs text-gray-500">Issue Date</p>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 mb-2">Authorized Signature</p>
                                            <div className="w-44 border-b-2 border-gray-800 pb-1 mb-1">
                                                <p className="font-bold text-gray-800 text-lg" style={{ fontFamily: 'cursive' }}>
                                                    AircoreDynamics
                                                </p>
                                            </div>
                                            <p className="text-xs text-gray-500">Platform Administrator</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visible Certificate (Site Design) */}
                    <div className="relative">
                        {/* Decorative Background Elements */}
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-light-primary/5 dark:bg-dark-primary/5 rounded-full blur-2xl"></div>
                        <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-blue-500/5 dark:bg-cyan-500/5 rounded-full blur-2xl"></div>
                        
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }} 
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 }}
                            className="relative bg-white dark:bg-dark-card rounded-2xl shadow-xl border-2 border-light-border dark:border-dark-border overflow-hidden"
                        >
                            {/* Elegant Top Border Decoration */}
                            <div className="h-1.5 bg-gradient-to-r from-light-primary via-blue-500 to-light-primary dark:from-dark-primary dark:via-cyan-500 dark:to-dark-primary"></div>
                            
                            <div className="p-4 md:p-6 lg:p-8">
                                {/* Verified Badge - More Elegant */}
                                <div className="flex justify-end mb-3 md:mb-4">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-green-500 blur-lg opacity-20 animate-pulse"></div>
                                        <div className="relative bg-gradient-to-br from-green-400 to-emerald-600 px-3 py-1.5 md:px-4 md:py-2 rounded-full flex items-center gap-1.5 shadow-lg">
                                            <ShieldCheckIcon className="h-4 w-4 md:h-5 md:w-5 text-white" />
                                            <span className="font-bold text-white text-xs md:text-sm tracking-wide">VERIFIED</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Certificate Header - More Professional */}
                                <div className="text-center mb-4 md:mb-6">
                                    <div className="relative inline-block mb-3 md:mb-4">
                                        <div className="absolute inset-0 bg-light-primary/20 dark:bg-dark-primary/20 blur-xl"></div>
                                        <AcademicCapIcon className="relative h-10 w-10 md:h-14 md:w-14 text-light-primary dark:text-dark-primary" />
                                    </div>
                                    
                                    <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 md:mb-3 tracking-tight">
                                        <span className="bg-gradient-to-r from-light-primary via-blue-600 to-light-primary dark:from-dark-primary dark:via-cyan-400 dark:to-dark-primary bg-clip-text text-transparent">
                                            Certificate of Completion
                                        </span>
                                    </h2>
                                    
                                    <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                        <div className="h-px w-8 md:w-12 bg-gradient-to-r from-transparent to-light-primary dark:to-dark-primary"></div>
                                        <div className="h-1.5 w-1.5 rounded-full bg-light-primary dark:bg-dark-primary"></div>
                                        <div className="h-px w-8 md:w-12 bg-gradient-to-l from-transparent to-light-primary dark:to-dark-primary"></div>
                                    </div>
                                </div>

                                {/* Certificate Body - More Structured */}
                                <div className="max-w-2xl mx-auto space-y-3 md:space-y-4 mb-5 md:mb-8">
                                    <p className="text-xs md:text-sm text-center opacity-60 italic">
                                        This is to proudly certify that
                                    </p>
                                    
                                    <div className="text-center">
                                        <div className="inline-block px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-light-primary/5 to-blue-500/5 dark:from-dark-primary/5 dark:to-cyan-500/5 rounded-xl">
                                            <p className="text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent break-words">
                                                {certificate.user.name}
                                            </p>
                                        </div>
                                        <div className="flex justify-center mt-2">
                                            <div className="h-0.5 w-20 md:w-32 bg-gradient-to-r from-transparent via-light-primary dark:via-dark-primary to-transparent rounded-full"></div>
                                        </div>
                                    </div>
                                    
                                    <p className="text-xs md:text-sm text-center opacity-60 italic">
                                        has successfully completed the course
                                    </p>
                                    
                                    <div className="bg-light-bg dark:bg-dark-bg rounded-xl p-3 md:p-5 border border-light-border dark:border-dark-border">
                                        <h3 className="text-base md:text-lg lg:text-xl font-bold text-center leading-relaxed break-words">
                                            {certificate.course.title}
                                        </h3>
                                    </div>
                                    
                                    <p className="text-xs md:text-sm text-center opacity-70 leading-relaxed px-2 md:px-4">
                                        and has demonstrated comprehensive understanding and proficiency in all required competencies
                                    </p>
                                </div>

                                {/* Certificate Footer - Professional Layout */}
                                <div className="border-t-2 border-light-border dark:border-dark-border pt-4 md:pt-5">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-3 text-center md:text-left">
                                        {/* Issue Date */}
                                        <div className="space-y-1">
                                            <p className="text-[10px] md:text-xs uppercase tracking-wider opacity-50 font-semibold">
                                                Issue Date
                                            </p>
                                            <div className="flex items-center justify-center md:justify-start gap-1.5">
                                                <svg className="h-3.5 w-3.5 md:h-4 md:w-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <p className="font-bold text-xs md:text-sm">
                                                    {new Date(certificate.issuedAt).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric', 
                                                        year: 'numeric' 
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Platform Badge */}
                                        <div className="flex flex-col items-center justify-center space-y-1">
                                            <div className="bg-gradient-to-br from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-500 px-3 py-1.5 md:px-4 md:py-2 rounded-lg shadow-md">
                                                <div className="flex items-center gap-1.5">
                                                    <AcademicCapIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-white" />
                                                    <span className="font-bold text-white text-xs md:text-sm">AircoreDynamics</span>
                                                </div>
                                            </div>
                                            <p className="text-[9px] md:text-[10px] opacity-50 max-w-[180px] leading-tight">
                                                Course indexed in MERLOT II (ID: 773476432)
                                            </p>
                                        </div>
                                        
                                        {/* Certificate ID */}
                                        <div className="space-y-1 md:text-right">
                                            <p className="text-[10px] md:text-xs uppercase tracking-wider opacity-50 font-semibold">
                                                Certificate ID
                                            </p>
                                            <div className="flex items-center justify-center md:justify-end gap-1.5">
                                                <svg className="h-3.5 w-3.5 md:h-4 md:w-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="font-mono font-bold text-[10px] md:text-xs break-all">
                                                    {certificate.certificateId}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Elegant Bottom Border Decoration */}
                            <div className="h-1.5 bg-gradient-to-r from-light-primary via-blue-500 to-light-primary dark:from-dark-primary dark:via-cyan-500 dark:to-dark-primary"></div>
                        </motion.div>
                    </div>

                    {/* Download Button */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex justify-center"
                    >
                        <button
                            onClick={handleDownloadPDF}
                            disabled={downloading}
                            className="group relative bg-gradient-to-r from-light-primary to-blue-600 dark:from-dark-primary dark:to-cyan-500 text-white font-bold px-6 py-2.5 md:px-8 md:py-3 rounded-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2 overflow-hidden"
                        >
                            {/* Button Glow Effect */}
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                            
                            <ArrowDownTrayIcon className="relative h-4 w-4 md:h-5 md:w-5 group-hover:animate-bounce" />
                            <span className="relative text-sm md:text-base">
                                {downloading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                                        <span className="hidden sm:inline">Generating...</span>
                                    </span>
                                ) : (
                                    <>
                                        <span className="hidden sm:inline">Download PDF</span>
                                        <span className="sm:hidden">Download</span>
                                    </>
                                )}
                            </span>
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
            </div>
        </>
    );
};

export default VerifyCertificatePage;